import { useMemo } from 'react';
import { OnScreenKeyboard } from '../../components/OnScreenKeyboard';
import { Tile, type TileState } from '../../components/Tile';
import type { BoardProps, InputKey } from '../../engine/types';
import { cx } from '../../utils/classnames';
import { aggregateLetterStates, evaluateGuess } from '../cipher/logic';
import { solvedCount, type BouquetMove, type BouquetState } from './logic';

const RANK: Record<TileState, number> = { absent: 0, present: 1, correct: 2 };

export function BouquetBoard({ state, status, dispatch, feedback }: BoardProps<BouquetState, BouquetMove>) {
  const playing = status === 'playing';
  const lastError = feedback?.tone === 'error' ? feedback.nonce : 0;
  const solved = solvedCount(state);

  // Merge keyboard tints across all four boards (best state wins).
  const letterStates = useMemo(() => {
    const merged: Record<string, TileState> = {};
    for (const answer of state.answers) {
      const m = aggregateLetterStates(state.guesses, answer);
      for (const [k, v] of Object.entries(m)) {
        if (!merged[k] || RANK[v] > RANK[merged[k]]) merged[k] = v;
      }
    }
    return merged;
  }, [state.guesses, state.answers]);

  const onKey = (key: InputKey) => {
    if (key.type === 'char') dispatch({ type: 'add', ch: key.value });
    else if (key.type === 'backspace') dispatch({ type: 'remove' });
    else dispatch({ type: 'submit' });
  };

  const rows = Array.from({ length: state.maxGuesses }, (_, r) => r);

  return (
    <div className="cv-board">
      <div className="cv-bouquet">
        {state.answers.map((answer, bi) => {
          const solvedAt = state.solvedAt[bi];
          const isSolved = solvedAt !== null;
          const committedToShow = isSolved ? solvedAt + 1 : state.guesses.length;
          return (
            <div key={bi} className="cv-bouquet__board" data-solved={isSolved ? 'true' : undefined}>
              {rows.map((r) => {
                const committed = r < committedToShow;
                const isCurrent = !isSolved && r === state.guesses.length && playing;
                const guess = committed ? state.guesses[r] : isCurrent ? state.current : '';
                const clues = committed ? evaluateGuess(state.guesses[r], answer) : null;
                return (
                  <div
                    key={isCurrent ? `cur-${lastError}` : r}
                    className={cx('cv-bouquet__row', isCurrent && lastError && 'cv-anim-shake')}
                  >
                    {Array.from({ length: state.length }, (_, c) => (
                      <Tile
                        key={c}
                        letter={guess[c]}
                        state={clues ? clues[c] : undefined}
                        filled={isCurrent && c < state.current.length}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <p className="cv-mono cv-faint" style={{ textAlign: 'center', fontSize: 'var(--text-sm)' }}>
        {solved}/{state.answers.length} solved · {state.guesses.length}/{state.maxGuesses} guesses
      </p>

      <div className="cv-board__status" data-tone={feedback?.tone === 'error' ? 'error' : undefined} aria-hidden="true">
        {feedback?.message ?? ''}
      </div>
      <p className="visually-hidden" aria-live="polite">
        {feedback?.message}
      </p>

      <OnScreenKeyboard onKey={onKey} letterStates={letterStates} disabled={!playing} />
    </div>
  );
}
